import { extractSigned, fetchRailsData, turboVisitPromise } from "../../lib/railsUtil.svelte";
import { interactionsReady, recordInteraction } from "../../lib/interactions.svelte";

export type UnsignedMatterType = "dpf" | "pcp";

export type LeadUnsignedProps = {
  matters: any[];
  events: Record<string, string>;
  case_type_path: string;
};

export const CloseActions = {
  "CONTINUE_SIGNING": "continueSigning",
  "VIEW_SIGNED": "viewSigned"
} as const;

export type CloseActionType = typeof CloseActions[keyof typeof CloseActions];

export class UnsignedMattersState {
  matters: any[];
  events: Record<string, string>;
  caseTypePath: string;
  matterType: UnsignedMatterType;
  mattersToSign: any[] = [];
  mattersToReject: any[] = [];
  mattersToAskLater: any[] = [];
  mattersSigned: any[] = [];
  currentStep = $state<symbol>();
  allowIndividualAskLater: boolean;
  rejectionsProcessed: boolean = false;
  steps = {
    offer: Symbol("offer"),
    signature: Symbol("signature"),
    success: Symbol("success"),
    rejection: Symbol("rejection"),
  }
  onComplete?: (action: CloseActionType) => void;
  nextSignableMatterType: UnsignedMatterType | null;
  defaultCloseAction: CloseActionType;
  
  /**
   * @param {Object} data - The data object containing matters, events, caseTypePath, matterType, and nextSignableMatterType.
   * @param {Array} data.matters - The list of matters to manage.
   * @param {Object} data.events - The events associated with the matters for creating interactions.
   * @param {string} data.caseTypePath - The path for the case type, used to navigate after signing.
   * @param {UnsignedMatterType} data.matterType - The type of matter (e.g., "dpf" or "pcp").
   * @param {UnsignedMatterType|null} [data.nextSignableMatterType=null] - The next matter type that can be signed after this one,
   * or null if this is the last.
   * @param {boolean|null} [data.allowIndividualAskLater=false] - Whether to allow "ask later" to be selected for individual matters
   * at the offer step. `false` means "Ask me later" applies to all matters at once.
   */
  constructor(data:{
    matters: any[],
    events: Record<string, string>,
    caseTypePath: string,
    matterType: UnsignedMatterType,
    nextSignableMatterType?: UnsignedMatterType | null,
    allowIndividualAskLater?: boolean | null
  }) {
    this.matters = data.matters;
    this.events = data.events;
    this.caseTypePath = data.caseTypePath;
    this.matterType = data.matterType;
    this.nextSignableMatterType = data.nextSignableMatterType || null;
    this.defaultCloseAction = this.nextSignableMatterType ? CloseActions.CONTINUE_SIGNING : CloseActions.VIEW_SIGNED;
    this.currentStep = this.steps.offer;
    this.allowIndividualAskLater = data.allowIndividualAskLater;
  }

  goNext() {
    if (this.mattersToSign.length) {
      this.currentStep = this.steps.signature;
    } else if (this.mattersToReject.length) {
      this.currentStep = this.steps.rejection;
    } else if (this.mattersSigned.length) {
      this.currentStep = this.steps.success;
    } else {
      if (this.mattersToAskLater.length) this.recordLaterInteraction();
      this.onComplete(this.defaultCloseAction);
    }
  }

  hasActionableMatters() {
    return (this.mattersToAskLater.length + this.mattersToSign.length) > 0
  }

  setActions({ onComplete }: { onComplete: (action: CloseActionType) => void }) {
    this.onComplete = onComplete;
  }

  get count() {
    return this.matters?.length || 0;
  }

  reset() {
    this.currentStep = this.steps.offer;
    this.mattersToSign = [];
    this.mattersToReject = [];
    this.mattersToAskLater = [];
    this.mattersSigned = [];
    this.rejectionsProcessed = false;
  }

  answerOffer(choices: Record<string, "yes" | "no" | "later">) {
    for(const matter of this.matters) {
      let choice = choices[matter.sfid];

      if (choice == 'yes') {
        this.mattersToSign.push(matter);
      } else if (choice == 'no') {
        this.mattersToReject.push(matter);
      } else if (choice == 'later') {
        this.mattersToAskLater.push(matter);
      }
    }
    if(this.mattersToSign.length) this.recordYesInteraction();
    if(this.mattersToReject.length) this.recordNoInteraction();
    this.goNext();
  }

  async doSign(svg: string) {
    const body = new FormData();
    body.append("signature", svg);
    await Promise.all(
      this.mattersToSign.map(async (matter) => {
        const result = await fetchRailsData(matter.sign_lead_url, { method: "POST", body });
        matter.contract_url = result.contract_url;
        this.mattersSigned.push(matter);
      })
    );
    this.mattersToSign = [];
    this.matters = this.matters.filter((m) => !this.mattersSigned.includes(m));
    if (!this.allowIndividualAskLater) {
      await this.processRejections();
    }
    this.goNext();
  }

  async doReject() {
    if (!this.mattersToReject || this.mattersToReject.length === 0) {
      return;
    }
    
    await this.processRejections();
  
    this.matters = this.matters.filter((matter) => !this.mattersToReject.includes(matter));
    this.mattersToReject = [];
    this.goNext();
  }

  askLater() {
    this.mattersToAskLater = this.mattersToAskLater.concat(this.mattersToReject);
    if (this.mattersToAskLater.length) {
      this.recordLaterInteraction();
      this.mattersToReject = [];
    }
    this.goNext();
  }

  async doSuccess(afterCloseAction: CloseActionType) {
    this.onComplete?.(afterCloseAction);
    
    if (afterCloseAction === CloseActions.VIEW_SIGNED) {
      await turboVisitPromise(this.caseTypePath);
    }
  }

  setup(onReady: () => void) {
    const completedWorkflow = ["later", "signed", "rejected"].map((shortName) =>
      extractSigned(this.events[shortName]).name
    );
    const withinLastDay = ({ at }: { at: number }) => at > Date.now() / 1000 - 60 * 60 * 24;

    interactionsReady().then((pastEvents) => {
      if (!this.count) return;
      if (pastEvents.getEvents(...completedWorkflow).some(withinLastDay)) {
        this.recordTaskShownInteraction();
      } else {
        onReady();
      }
    });
  }

  get offerStep() {
    return this.steps.offer;
  }

  get signatureStep() {
    return this.steps.signature;
  }

  get successStep() {
    return this.steps.success;
  }

  get rejectionStep() {
    return this.steps.rejection;
  }

  recordTaskShownInteraction() {
    recordInteraction(this.events.task_shown);
  }

  recordFeeCapAcknowledgedInteraction() {
    recordInteraction(this.events.fee_cap_acknowledged);
  }

  recordOpenInteraction() {
    recordInteraction(this.events.open);
  }

  recordLaterInteraction() {
    recordInteraction(this.events.later);
  }

  recordYesInteraction() {
    recordInteraction(this.events.yes);
  }

  recordSignedInteraction() {
    recordInteraction(this.events.signed);
  }

  recordNoInteraction() {
    recordInteraction(this.events.no);
  }

  recordRejectedInteraction() {
    recordInteraction(this.events.rejected);
  }

  recordReopenInteraction() {
    recordInteraction(this.events.reopen);
  }

  private async processRejections() {
    if (!this.rejectionsProcessed && this.mattersToReject.length > 0) {
      await Promise.all(
        this.mattersToReject.map((matter) => 
          fetchRailsData(matter.reject_lead_url, { method: "POST" })
        )
      );
      this.rejectionsProcessed = true;
    }
  }
}